import type { ProductKey, UiProductType } from '../types';

import { PRODUCT_LOGO_MAP, type ProductLogoConfig } from './productConfig';

/**
 * Product keys of apps that are supported by the backend and should be visible in the UI.
 * Derived from the backend's supported apps enum (context IDs).
 */
const VISIBLE_PRODUCT_KEYS: ReadonlySet<ProductKey> = new Set<ProductKey>([
  'cmdb', // ASSETS
  'avp', // ATLASSIAN_ANALYTICS
  'bitbucket', // BITBUCKET
  'cloud-admin', // CLOUD_ADMIN
  'compass', // COMPASS
  'confluence', // CONFLUENCE
  'jira-customer-service', // CSM
  'mercury', // FOCUS / MERCURY
  'goal', // GOALS
  'jira-software', // JIRA
  'jira-align', // JIRA_ALIGN
  'jira-product-discovery', // JIRA_PRODUCT_DISCOVERY
  'jira-servicedesk', // JIRA_SERVICE_MANAGEMENT
  'loom', // LOOM
  'opsgenie', // OPSGENIE
  'project', // PROJECTS
  'radar', // TALENT
  'rovo', // ROVO
  'devai', // ROVO_DEV
  'statuspage', // STATUSPAGE
  'studio', // STUDIO
  'teams', // TEAMS
  'trello', // TRELLO
]);

/**
 * Returns a filtered subset of PRODUCT_LOGO_MAP keyed by UiProductType,
 * containing only the apps that are supported by the backend (visible apps).
 * Using UiProductType as the client-side product identifier for consistency.
 */
export const getVisibleApps = (): Record<UiProductType, ProductLogoConfig> => {
  return Object.fromEntries(
    Object.entries(PRODUCT_LOGO_MAP)
      .filter(([, config]) => VISIBLE_PRODUCT_KEYS.has(config.productKey))
      // PRODUCT_LOGO_MAP keys are Lowercase<UiProductType>, so toUpperCase() gives UiProductType
      .map(([key, config]) => [key.toUpperCase() as UiProductType, config]),
  ) as Record<UiProductType, ProductLogoConfig>;
};

/**
 * Checks whether a given product key belongs to a visible (supported) app.
 */
export const isVisibleApp = (productKey: ProductKey): boolean => {
  return VISIBLE_PRODUCT_KEYS.has(productKey);
};
